/**
 * GeneralImage Component
 *
 * A reusable image component with built-in error handling and default fallback.
 * Handles various HTML image attributes and common event handlers.
 *
 * Props:
 * @param {string} src - The source URL of the image.
 * @param {string} alt - Alternative text for the image (default: "").
 * @param {string} className - CSS classes for styling the image.
 * @param {string} id - Optional id attribute.
 * @param {React.CSSProperties} style - Inline styles.
 * @param {number | string} width - Width of the image.
 * @param {number | string} height - Height of the image.
 * @param {"sync" | "async" | "auto"} decoding - Decoding strategy.
 * @param {"eager" | "lazy" | "auto"} loading - Lazy-loading behavior.
 * @param {string} crossOrigin - Cross-origin setting.
 * @param {string} referrerPolicy - Referrer policy.
 * @param {string} useMap - Image map name.
 * @param {string} role - ARIA role.
 * @param {number} tabIndex - Tab index.
 * @param {boolean} draggable - Draggable attribute.
 * @param {string} title - Tooltip text.
 * @param {React.Ref<HTMLImageElement>} ref - React ref.
 * @param {function} onClick, onDoubleClick, onMouseDown, onMouseUp, onMouseEnter, onMouseLeave,
 *   onMouseMove, onContextMenu, onFocus, onBlur, onLoad, onError - Event handlers.
 *
 * Behavior:
 * - Displays an image using the provided `src`.
 * - Automatically replaces the image with a default fallback if it fails to load.
 * - Supports all common image HTML attributes and event handlers.
 * - Updates the image source if the `src` prop changes.
 *
 * Usage:
 * ```tsx
 * <GeneralImage
 *   src="/images/drug.png"
 *   alt="Drug Image"
 *   className="h-24 w-24 rounded-xl object-cover"
 *   onError={(e) => console.log("Image failed to load", e)}
 * />
 * ```
 *
 * Notes:
 * - Uses `BASE_LOCAL_IMAGES_DIR` and `default_Image_For_All_IMG` for fallback.
 * - Fully compatible with TailwindCSS or custom CSS styling.
 * - Ideal for use in drug cards, lists, and anywhere images might fail to load.
 */

import React, { useEffect, useState } from "react";
import { GeneralImageInterface } from "../../types/GeneralComponents";
import { BASE_LOCAL_IMAGES_DIR } from "../../utils/constants/CommonConstants";
import { default_Image_For_All_IMG } from "../../utils/constants/Images";

const GeneralImage: React.FC<GeneralImageInterface> = ({
  src,
  alt = "",
  className = "",
  id,
  style,
  width,
  height,
  decoding,
  loading,
  crossOrigin,
  referrerPolicy,
  useMap,
  role,
  tabIndex,
  draggable,
  title,
  onClick,
  onDoubleClick,
  onMouseDown,
  onMouseUp,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  onContextMenu,
  onFocus,
  onBlur,
  onLoad,
  onError,
  ref,
  fullscreen = false,
}) => {
  const [imgSrc, setImgSrc] = useState<string>(src);

  useEffect(() => {
    setImgSrc(src);
  }, [src]);

  const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (fullscreen) {
      window.open(imgSrc, "_blank");
    } else {
      onClick?.(e);
    }
  };

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={` ${className} ${fullscreen ? "cursor-zoom-in" : ""} transition-transform duration-300 ease-in-out will-change-transform hover:-translate-y-1 hover:shadow-sm`}
      id={id}
      style={style}
      width={width}
      height={height}
      decoding={decoding}
      loading={loading}
      crossOrigin={crossOrigin}
      referrerPolicy={referrerPolicy}
      useMap={useMap}
      role={role}
      tabIndex={tabIndex}
      draggable={draggable}
      title={title}
      ref={ref}
      onClick={handleClick}
      onDoubleClick={onDoubleClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      onContextMenu={onContextMenu}
      onFocus={onFocus}
      onBlur={onBlur}
      onLoad={onLoad}
      onError={(e) => {
        setImgSrc(`${BASE_LOCAL_IMAGES_DIR}/${default_Image_For_All_IMG}`);
        onError?.(e);
      }}
    />
  );
};

export default GeneralImage;
